import localStorageService from './storage'
import currencyApi, { Currency, CurrencyExchange } from './currencyApi'
import { CURRENCY_CONFIG } from 'config/currency'

interface CachedCurrencyData {
  currencies: Currency[]
  lastUpdated: number
  exchangeRates: Record<string, CachedExchangeRate>
  allYearlyRates: Record<string, CachedExchangeRate>
}

interface CachedExchangeRate {
  data: CurrencyExchange
  lastUpdated: number
}

class CurrencyCacheService {
  private readonly CACHE_KEY = 'currency_cache'
  private readonly CURRENCIES_TTL = CURRENCY_CONFIG.CACHE_TTL.CURRENCIES
  private readonly EXCHANGE_RATES_TTL = CURRENCY_CONFIG.CACHE_TTL.EXCHANGE_RATES

  private getCachedData(): CachedCurrencyData {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY)
      return cached ? JSON.parse(cached) : this.getDefaultCache()
    } catch (error) {
      console.error('Failed to parse cached currency data:', error)
      return this.getDefaultCache()
    }
  }

  private getDefaultCache(): CachedCurrencyData {
    return {
      currencies: [],
      lastUpdated: 0,
      exchangeRates: {},
      allYearlyRates: {},
    }
  }

  private setCachedData(data: CachedCurrencyData): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(data))
    } catch (error) {
      console.error('Failed to cache currency data:', error)
    }
  }

  private isExpired(timestamp: number, ttl: number): boolean {
    return Date.now() - timestamp > ttl
  }

  async getCurrencies(forceRefresh = false): Promise<Currency[]> {
    const cached = this.getCachedData()
    
    if (!forceRefresh && !this.isExpired(cached.lastUpdated, this.CURRENCIES_TTL)) {
      return cached.currencies
    }

    try {
      const currencies = await currencyApi.getAllCurrencies()
      
      if (currencies && currencies.length > 0) {
        const updatedCache = {
          ...cached,
          currencies,
          lastUpdated: Date.now(),
        }
        this.setCachedData(updatedCache)
        return currencies
      }
    } catch (error) {
      console.error('Failed to fetch currencies from API, using cached data:', error)
    }

    return cached.currencies
  }

  async getCurrency(currencyCode: string, forceRefresh = false): Promise<Currency | null> {
    const currencies = await this.getCurrencies(forceRefresh)
    return currencies.find(c => c.code === currencyCode) || null
  }

  async getAllYearlyExchangeRates(currencyCode: string, forceRefresh = false): Promise<CurrencyExchange | null> {
    const cached = this.getCachedData()
    const cacheKey = `${currencyCode}_all_yearly`
    const cachedRate = cached.allYearlyRates[cacheKey]

    if (!forceRefresh && cachedRate && !this.isExpired(cachedRate.lastUpdated, this.EXCHANGE_RATES_TTL)) {
      return cachedRate.data
    }

    try {
      const exchangeRates = await currencyApi.getExchangeRates(currencyCode)
      
      if (exchangeRates) {
        const updatedCache = {
          ...cached,
          allYearlyRates: {
            ...cached.allYearlyRates,
            [cacheKey]: {
              data: exchangeRates,
              lastUpdated: Date.now(),
            },
          },
        }
        this.setCachedData(updatedCache)
        return exchangeRates
      }
    } catch (error) {
      console.error(`Failed to fetch all yearly exchange rates for ${currencyCode}, using cached data:`, error)
    }

    return cachedRate?.data || null
  }

  async getExchangeRates(
    currencyCode: string,
    startYear?: number,
    endYear?: number,
    forceRefresh = false
  ): Promise<CurrencyExchange | null> {
    const allYearlyRates = await this.getAllYearlyExchangeRates(currencyCode, forceRefresh)
    
    if (allYearlyRates) {
      if (startYear || endYear) {
        const filteredValues: Record<string, number> = {}
        
        Object.entries(allYearlyRates.values).forEach(([year, rate]) => {
          const yearNum = parseInt(year)
          if ((!startYear || yearNum >= startYear) && (!endYear || yearNum <= endYear)) {
            filteredValues[year] = rate
          }
        })
        
        return {
          ...allYearlyRates,
          values: filteredValues,
        }
      }
      
      return allYearlyRates
    }

    const cached = this.getCachedData()
    const cacheKey = `${currencyCode}_${startYear || 'all'}_${endYear || 'all'}`
    const cachedRate = cached.exchangeRates[cacheKey]

    if (!forceRefresh && cachedRate && !this.isExpired(cachedRate.lastUpdated, this.EXCHANGE_RATES_TTL)) {
      return cachedRate.data
    }

    try {
      const exchangeRates = await currencyApi.getExchangeRates(currencyCode, startYear, endYear)
      
      if (exchangeRates) {
        const updatedCache = {
          ...cached,
          exchangeRates: {
            ...cached.exchangeRates,
            [cacheKey]: {
              data: exchangeRates,
              lastUpdated: Date.now(),
            },
          },
        }
        this.setCachedData(updatedCache)
        return exchangeRates
      }
    } catch (error) {
      console.error(`Failed to fetch exchange rates for ${currencyCode}, using cached data:`, error)
    }

    return cachedRate?.data || null
  }

  async refreshCurrencies(): Promise<Currency[]> {
    return this.getCurrencies(true)
  }

  async refreshExchangeRates(currencyCode: string, startYear?: number, endYear?: number): Promise<CurrencyExchange | null> {
    return this.getExchangeRates(currencyCode, startYear, endYear, true)
  }

  async refreshAllYearlyExchangeRates(currencyCode: string): Promise<CurrencyExchange | null> {
    return this.getAllYearlyExchangeRates(currencyCode, true)
  }

  clearCache(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY)
    } catch (error) {
      console.error('Failed to clear currency cache:', error)
    }
  }
}

export default new CurrencyCacheService()
